// ─── REVISIÓN DE LA LECTURA AUTOMÁTICA DE DOCUMENTOS ─────────────────────────
// Muestra lo que la IA leyó de los documentos de una orden, artículo por
// artículo, junto a lo que hoy tiene la app. Nada se guarda hasta "Aplicar":
//   - Datos del embarque (cantidades, colores, ficha técnica, B/L) → /embarques
//   - Cant. embarcada, F. embarque y plazo → Cuentas por Pagar (solo lo marcado)
import { useEffect, useMemo, useState } from "react";
import { api } from "../auth";

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
const num = (n, d = 2) =>
  n === null || n === undefined || n === "" || !isFinite(Number(n))
    ? "—"
    : Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: d });
const usd = (n) => (isFinite(Number(n)) ? Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—");
// "2026-08-13" → "13/08/2026"
const isoADmy = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const esKg = (u) => /kg|kilo/i.test(String(u || ""));
const esMetro = (u) => /^\s*(m|mt|mts|metro|metros|mtrs?)\b/i.test(String(u || ""));
const ORIGEN_FECHA = { bl: "del B/L", factura: "fecha de la factura", packing: "del packing list", otro: "", desconocida: "" };

// Cantidad del documento en la unidad que usa la app para ese artículo.
function cantidadEnUnidad(doc, unidadApp) {
  if (esKg(unidadApp) && doc.kilos != null) return { valor: doc.kilos, unidad: "KG" };
  if (esMetro(unidadApp) && doc.metros != null) return { valor: doc.metros, unidad: "MT" };
  if (doc.cantidad_factura != null) return { valor: doc.cantidad_factura, unidad: doc.unidad_factura || "" };
  if (doc.kilos != null) return { valor: doc.kilos, unidad: "KG" };
  if (doc.metros != null) return { valor: doc.metros, unidad: "MT" };
  return { valor: null, unidad: "" };
}

// Factura de CxP que corresponde a un artículo de la OC (por tela o referencia).
function facturaPara(art, facturas) {
  if (!art) return null;
  const claves = [norm(art.tela), norm(art.ref)].filter((x) => x.length >= 3);
  const hit = facturas.find((f) => {
    const t = [norm(f.tela), norm(f.refProveedor)].filter(Boolean);
    return t.some((x) => claves.some((c) => x.includes(c) || c.includes(x)));
  });
  return hit || null;
}

export default function RevisionLectura({ noOrden, lectura, articulosOrden, calcularFacturaCxP, onClose, onAplicado }) {
  const r = lectura?.resultado || {};
  const docsArt = r.articulos || [];
  const [facturas, setFacturas] = useState([]);
  const [cargandoCxp, setCargandoCxp] = useState(true);
  const [asignacion, setAsignacion] = useState(() => docsArt.map((a) => a.art_key || ""));
  const [cxpSel, setCxpSel] = useState({}); // índice → factura_key | "" (no actualizar)
  const [marcas, setMarcas] = useState({}); // `${i}:${campo}` → bool
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState("");

  const artPorKey = useMemo(() => Object.fromEntries((articulosOrden || []).map((a) => [a._key, a])), [articulosOrden]);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/cxp-facturas");
        const n = String(noOrden).trim().toUpperCase();
        const pref = (s) => (/^\d+/.exec(String(s || "").trim()) || [""])[0];
        const todas = Array.isArray(data?.facturas) ? data.facturas : [];
        const exactas = todas.filter((f) => String(f.orderNo || "").trim().toUpperCase() === n);
        setFacturas(exactas.length ? exactas : todas.filter((f) => pref(f.orderNo) && pref(f.orderNo) === pref(noOrden)));
      } catch (e) {
        setFacturas([]);
      }
      setCargandoCxp(false);
    })();
  }, [noOrden]);

  const fechaEmbarque = isoADmy(r.embarque?.fecha_embarque);
  const plazoDoc = r.factura?.plazo_pago_dias;

  // Factura de CxP elegida para cada artículo (automática hasta que el usuario elija).
  const facturaDe = (i) => {
    if (cxpSel[i] !== undefined) return facturas.find((f) => f._key === cxpSel[i]) || null;
    return facturaPara(artPorKey[asignacion[i]], facturas);
  };

  // Valores por default de las casillas: marcado si el documento trae el dato y es distinto a lo actual.
  const marcado = (i, campo, porDefecto) => (marcas[`${i}:${campo}`] !== undefined ? marcas[`${i}:${campo}`] : porDefecto);
  const alternar = (i, campo, actual) => setMarcas((m) => ({ ...m, [`${i}:${campo}`]: !actual }));

  const filas = docsArt.map((d, i) => {
    const art = artPorKey[asignacion[i]];
    const fac = facturaDe(i);
    const unidadApp = fac?.unidad || art?.unidad;
    const cant = cantidadEnUnidad(d, unidadApp);
    const cambiaCant = cant.valor != null && fac && Math.abs((+fac.cantEmbarcada || 0) - cant.valor) > 0.005;
    const cambiaFecha = !!fechaEmbarque && fac && fac.fEmbarque !== fechaEmbarque;
    const cambiaPlazo = plazoDoc != null && fac && +fac.plazo !== +plazoDoc;
    const tieneFicha = d.composicion || d.ancho_total_m || d.peso_gsm;
    return { d, i, art, fac, cant, cambiaCant, cambiaFecha, cambiaPlazo, tieneFicha };
  });

  const aplicar = async () => {
    setError("");
    const articulos = [];
    const cxp = [];
    for (const f of filas) {
      if (!f.art) continue;
      const { d, i } = f;
      const datos = {
        codigo_documento: d.codigo_documento,
        descripcion: d.descripcion,
        kilos: d.kilos,
        metros: d.metros,
        rollos: d.rollos,
        cantidad_embarcada: f.cant.valor,
        unidad: f.cant.unidad,
        precio_unitario: d.precio_unitario,
        unidad_precio: d.unidad_precio,
        importe: d.importe,
      };
      if (marcado(i, "ficha", !!f.tieneFicha)) {
        Object.assign(datos, { composicion: d.composicion, ancho_total_m: d.ancho_total_m, ancho_util_m: d.ancho_util_m, peso_gsm: d.peso_gsm });
      }
      if (marcado(i, "colores", (d.colores || []).length > 0)) datos.colores = d.colores;
      articulos.push({ art_key: f.art._key, datos });
      if (f.fac) {
        const cambios = {};
        if (f.cant.valor != null && marcado(i, "cant", f.cambiaCant)) cambios.cantEmbarcada = f.cant.valor;
        if (fechaEmbarque && marcado(i, "fecha", f.cambiaFecha)) cambios.fEmbarque = fechaEmbarque;
        if (plazoDoc != null && marcado(i, "plazo", f.cambiaPlazo)) cambios.plazo = plazoDoc;
        if (Object.keys(cambios).length) cxp.push({ factura_key: f.fac._key, cambios });
      }
    }
    if (!articulos.length) return setError("Asigna al menos un artículo del documento a un artículo de la orden.");
    const e = r.embarque || {};
    const fa = r.factura || {};
    const embarque = {
      factura_numero: fa.numero,
      fecha_factura: fa.fecha,
      incoterm: fa.incoterm,
      total_usd: fa.total,
      descuento_usd: fa.descuento,
      bl_numero: e.bl_numero,
      fecha_embarque: e.fecha_embarque,
      origen_fecha_embarque: e.origen_fecha_embarque,
      buque: e.buque,
      contenedores: e.contenedores,
      puerto_origen: e.puerto_origen,
      puerto_destino: e.puerto_destino,
      eta: e.eta,
    };
    setAplicando(true);
    try {
      const res = await api.post(`/ordenes/${encodeURIComponent(noOrden)}/aplicar-lectura`, { lectura_id: lectura.id, embarque, articulos, cxp });
      onAplicado?.(res);
    } catch (err) {
      setError(/"detail":"([^"]+)"/.exec(String(err.message))?.[1] || "No se pudo aplicar. Intenta de nuevo.");
      setAplicando(false);
    }
  };

  const avisos = [...(lectura?.avisos_app || []), ...(r.avisos || [])];
  const ordenDoc = r.orden_en_documentos;
  const ordenDistinta = ordenDoc && norm(ordenDoc) !== norm(noOrden);
  const usados = asignacion.filter(Boolean);

  const Casilla = ({ i, campo, porDefecto, disabled }) => {
    const v = marcado(i, campo, porDefecto);
    return (
      <input
        type="checkbox"
        checked={!disabled && v}
        disabled={disabled}
        onChange={() => alternar(i, campo, v)}
        data-testid={`rev-${i}-${campo}`}
        className="h-4 w-4"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-3 overflow-y-auto" data-testid="revision-lectura">
      <div className="bg-white rounded-2xl w-full max-w-5xl p-5 space-y-4 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Revisar lectura de documentos</p>
            <p className="text-sm text-slate-700">
              Orden <b>{noOrden}</b>
              {r.proveedor_documento ? ` · ${r.proveedor_documento}` : ""}
            </p>
            <p className="text-[11px] text-slate-400">
              Nada se guarda hasta que des "Aplicar". Marca solo lo que quieras actualizar.
            </p>
          </div>
          <button onClick={onClose} disabled={aplicando} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
            ×
          </button>
        </div>

        {/* Datos generales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[
            ["Factura", r.factura?.numero],
            ["Fecha factura", isoADmy(r.factura?.fecha)],
            ["Total factura", r.factura?.total != null ? `USD ${usd(r.factura.total)}` : null],
            ["Descuento", r.factura?.descuento ? `USD ${usd(r.factura.descuento)}` : null],
            ["B/L", r.embarque?.bl_numero],
            ["F. embarque", fechaEmbarque ? `${fechaEmbarque} ${ORIGEN_FECHA[r.embarque?.origen_fecha_embarque] ? `(${ORIGEN_FECHA[r.embarque.origen_fecha_embarque]})` : ""}` : null],
            ["Buque", r.embarque?.buque],
            ["Contenedor(es)", (r.embarque?.contenedores || []).join(", ")],
            ["Origen → destino", [r.embarque?.puerto_origen, r.embarque?.puerto_destino].filter(Boolean).join(" → ")],
            ["ETA", isoADmy(r.embarque?.eta)],
            ["Plazo de pago", plazoDoc != null ? `${plazoDoc} días` : null],
            ["Orden en documentos", ordenDoc],
          ].map(([k, v]) => (
            <div key={k} className="bg-slate-50 rounded-lg px-2 py-1.5">
              <p className="text-[10px] text-slate-500">{k}</p>
              <p className={`font-semibold ${k === "Orden en documentos" && ordenDistinta ? "text-amber-700" : "text-slate-800"}`}>{v || "—"}</p>
            </div>
          ))}
        </div>

        {(avisos.length > 0 || ordenDistinta) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-1" data-testid="rev-avisos">
            <p className="font-semibold">⚠️ Revisa antes de aplicar</p>
            {ordenDistinta && <p>• Los documentos dicen orden {ordenDoc}, distinta a {noOrden}.</p>}
            {avisos.map((a, k) => (
              <p key={k}>• {a}</p>
            ))}
          </div>
        )}

        {cargandoCxp && <p className="text-xs text-slate-400">Cargando Cuentas por Pagar…</p>}

        {filas.map((f) => {
          const { d, i, art, fac, cant } = f;
          const totalAntes = fac ? calcularFacturaCxP(fac).totalReal : null;
          const totalDespues = fac && cant.valor != null ? calcularFacturaCxP({ ...fac, cantEmbarcada: cant.valor }).totalReal : null;
          const precioArt = art ? +(art.fobUSD || art.precioUnitario) : null;
          const precioDistinto = precioArt && d.precio_unitario != null && Math.abs(precioArt - d.precio_unitario) / precioArt > 0.005;
          const pedidos = Object.fromEntries((art?.colores || []).map((c) => [norm(c.color || c.diseno), c]));
          return (
            <div key={i} className={`border rounded-xl p-3 space-y-2 ${art ? "border-slate-200" : "border-amber-300 bg-amber-50/40"}`} data-testid={`rev-art-${i}`}>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <p className="text-sm font-semibold text-slate-800">
                  📄 {d.codigo_documento || "—"} <span className="font-normal text-slate-500">{d.descripcion}</span>
                </p>
                <label className="text-xs text-slate-600 flex items-center gap-1">
                  Artículo de la orden:
                  <select
                    value={asignacion[i]}
                    onChange={(e) => setAsignacion((a) => a.map((x, k) => (k === i ? e.target.value : x)))}
                    className="border border-slate-300 rounded-lg px-2 py-1 text-xs"
                    data-testid={`rev-asignar-${i}`}
                  >
                    <option value="">— No aplicar —</option>
                    {(articulosOrden || []).map((a) => (
                      <option key={a._key} value={a._key} disabled={usados.includes(a._key) && asignacion[i] !== a._key}>
                        {a.tela}
                        {a.ref ? ` (${a.ref})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {d.motivo_asignacion && <p className="text-[11px] text-slate-400">{d.motivo_asignacion}</p>}

              {art && (
                <>
                  <label className="text-xs text-slate-600 flex items-center gap-1 flex-wrap">
                    Factura de Cuentas por Pagar:
                    <select
                      value={fac?._key || ""}
                      onChange={(e) => setCxpSel((s) => ({ ...s, [i]: e.target.value }))}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-xs"
                      data-testid={`rev-cxp-${i}`}
                    >
                      <option value="">— No actualizar Cuentas por Pagar —</option>
                      {facturas.map((x) => (
                        <option key={x._key} value={x._key}>
                          {x.orderNo} · {x.tela} · {x.proveedor}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 bg-slate-50">
                          <th className="text-left px-2 py-1 font-semibold">Dato</th>
                          <th className="text-left px-2 py-1 font-semibold">Hoy en la app</th>
                          <th className="text-left px-2 py-1 font-semibold">Documento</th>
                          <th className="text-center px-2 py-1 font-semibold">Aplicar</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-1.5">Cant. embarcada (CxP)</td>
                          <td className="px-2 py-1.5">
                            {fac ? `${num(fac.cantEmbarcada)} ${fac.unidad || ""}` : "—"}
                            {fac && <span className="text-slate-400"> · pedida {num(fac.cantPedida)}</span>}
                          </td>
                          <td className="px-2 py-1.5 font-semibold">
                            {cant.valor != null ? `${num(cant.valor)} ${cant.unidad}` : "—"}
                            <span className="font-normal text-slate-400">
                              {" "}
                              {d.kilos != null && d.metros != null ? `(${num(d.kilos)} kg · ${num(d.metros)} m` : ""}
                              {d.rollos != null ? `${d.kilos != null && d.metros != null ? " · " : "("}${d.rollos} rollos)` : d.kilos != null && d.metros != null ? ")" : ""}
                            </span>
                            {totalAntes != null && totalDespues != null && Math.abs(totalAntes - totalDespues) > 0.005 && (
                              <p className="font-normal text-slate-500">
                                Total real CxP: USD {usd(totalAntes)} → <b>USD {usd(totalDespues)}</b>
                              </p>
                            )}
                          </td>
                          <td className="text-center">
                            <Casilla i={i} campo="cant" porDefecto={f.cambiaCant} disabled={!fac || cant.valor == null} />
                          </td>
                        </tr>
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-1.5">F. embarque (CxP)</td>
                          <td className="px-2 py-1.5">{fac?.fEmbarque || "—"}</td>
                          <td className="px-2 py-1.5 font-semibold">
                            {fechaEmbarque || "—"}
                            {fechaEmbarque && ORIGEN_FECHA[r.embarque?.origen_fecha_embarque] && (
                              <span className="font-normal text-slate-400"> ({ORIGEN_FECHA[r.embarque.origen_fecha_embarque]})</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Casilla i={i} campo="fecha" porDefecto={f.cambiaFecha} disabled={!fac || !fechaEmbarque} />
                          </td>
                        </tr>
                        {plazoDoc != null && (
                          <tr className="border-t border-slate-100">
                            <td className="px-2 py-1.5">Plazo (CxP)</td>
                            <td className="px-2 py-1.5">{fac?.plazo ? `${fac.plazo} días` : "—"}</td>
                            <td className="px-2 py-1.5 font-semibold">{plazoDoc} días</td>
                            <td className="text-center">
                              <Casilla i={i} campo="plazo" porDefecto={f.cambiaPlazo} disabled={!fac} />
                            </td>
                          </tr>
                        )}
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-1.5">Precio</td>
                          <td className="px-2 py-1.5">{precioArt ? `USD ${precioArt}` : "—"}</td>
                          <td className={`px-2 py-1.5 font-semibold ${precioDistinto ? "text-amber-700" : ""}`}>
                            {d.precio_unitario != null ? `USD ${d.precio_unitario}${d.unidad_precio ? ` / ${d.unidad_precio}` : ""}` : "—"}
                            {d.importe != null && <span className="font-normal text-slate-400"> · importe USD {usd(d.importe)}</span>}
                            {precioDistinto && <span className="font-normal"> · distinto al de la OC</span>}
                          </td>
                          <td className="text-center text-[10px] text-slate-400">solo aviso</td>
                        </tr>
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-1.5">Ficha técnica</td>
                          <td className="px-2 py-1.5">
                            {[art.composicion, art.ancho && `ancho ${art.ancho}`, art.peso && `${art.peso} gsm`].filter(Boolean).join(" · ") || "—"}
                          </td>
                          <td className="px-2 py-1.5 font-semibold">
                            {[
                              d.composicion,
                              d.ancho_total_m && `ancho ${Number(d.ancho_total_m).toFixed(2)} m${d.ancho_util_m ? ` (útil ${Number(d.ancho_util_m).toFixed(2)})` : ""}`,
                              d.peso_gsm && `${num(d.peso_gsm, 0)} gsm`,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </td>
                          <td className="text-center">
                            <Casilla i={i} campo="ficha" porDefecto={!!f.tieneFicha} disabled={!f.tieneFicha} />
                          </td>
                        </tr>
                        <tr className="border-t border-slate-100 align-top">
                          <td className="px-2 py-1.5">Colores embarcados</td>
                          <td className="px-2 py-1.5 text-slate-500" colSpan={2}>
                            {(d.colores || []).length === 0 ? (
                              "—"
                            ) : (
                              <table className="text-[11px] min-w-[340px]">
                                <thead>
                                  <tr className="text-slate-400">
                                    <th className="text-left pr-3 font-medium">Color</th>
                                    <th className="text-right pr-3 font-medium">Pedido</th>
                                    <th className="text-right pr-3 font-medium">Embarcado</th>
                                    <th className="text-right font-medium">Rollos</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {d.colores.map((c, k) => {
                                    const p = pedidos[norm(c.color)];
                                    const emb = esKg(cant.unidad) ? c.kilos : esMetro(cant.unidad) ? c.metros : c.kilos ?? c.metros;
                                    return (
                                      <tr key={k}>
                                        <td className="pr-3 text-slate-700">{c.color}</td>
                                        <td className="pr-3 text-right">{p ? num(p.cantidad) : <span className="text-amber-700">no pedido</span>}</td>
                                        <td className="pr-3 text-right font-semibold text-slate-700">{num(emb)}</td>
                                        <td className="text-right">{c.rollos ?? "—"}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                          <td className="text-center">
                            <Casilla i={i} campo="colores" porDefecto={(d.colores || []).length > 0} disabled={!(d.colores || []).length} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {(d.fuentes || []).length > 0 && <p className="text-[10px] text-slate-400">Fuente: {d.fuentes.join(" · ")}</p>}
                </>
              )}
            </div>
          );
        })}

        {docsArt.length === 0 && <p className="text-sm text-slate-500">No se encontraron artículos en los documentos.</p>}

        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-slate-400">
            Ficha técnica, colores y datos del B/L se guardan como datos del embarque de la orden (no cambian lo pedido en la OC).
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={aplicando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600">
              Cancelar
            </button>
            <button
              type="button"
              onClick={aplicar}
              disabled={aplicando || cargandoCxp}
              data-testid="rev-aplicar"
              className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {aplicando ? "Aplicando…" : "Aplicar lo marcado"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
